import React, { useState, useRef, useEffect, useCallback } from "react";
import "./style/hostCombobox.scss";
import type { HostOption } from "./hostSelector";
import { useStrings } from "../../../hooks/useStrings";

interface HostComboboxProps {
  selectedHostId: string;
  selectedHostName: string;
  onHostSelect: (hostId: string, firstName: string, lastName: string) => void;
  onClearHost: () => void;
  loadHostOptions: (searchTerm: string) => Promise<HostOption[]>;
  btnColor: string;
  fieldId: string;
  dataTestId?: string;
  className?: string;
  disabled?: boolean;
}

const getAvatarValue = (name: string): string => {
  if (!name) return "";
  const names = name.trim().split(" ");
  if (names.length >= 2) {
    return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase();
  }
  return name.charAt(0).toUpperCase();
};

const HostCombobox: React.FC<HostComboboxProps> = ({
  selectedHostId,
  selectedHostName,
  onHostSelect,
  onClearHost,
  loadHostOptions,
  btnColor,
  fieldId,
  dataTestId,
  className,
  disabled = false,
}) => {
  const S = useStrings();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<HostOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // Auto-focus search input when panel opens
  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  const fetchOptions = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (term.length < 3) {
        setOptions([]);
        setIsLoading(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          const results = await loadHostOptions(term);
          setOptions(results || []);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    },
    [loadHostOptions],
  );

  const handleTriggerClick = () => {
    if (!isOpen) {
      setSearchTerm("");
      setOptions([]);
    }
    setIsOpen((prev) => !prev);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    fetchOptions(term);
  };

  const handleItemClick = (opt: HostOption) => {
    onHostSelect(opt.value, opt.firstName, opt.lastName);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClearHost();
  };

  return (
    <div
      className={`host-combobox${className ? ` ${className}` : ""}${disabled ? " host-combobox--disabled" : ""}`}
      ref={wrapperRef}
      data-testid={`HC-wrapper-${dataTestId}`}
    >
      {/* Trigger */}
      <button
        type="button"
        id={fieldId}
        className="host-combobox-trigger"
        style={isOpen ? { borderColor: btnColor } : undefined}
        onClick={handleTriggerClick}
        disabled={disabled}
        data-testid={`HC-trigger-${dataTestId}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedHostId ? (
          <span className="host-combobox-selected-value">
            <span
              className="host-combobox-avatar"
              style={{ backgroundColor: btnColor }}
            >
              {getAvatarValue(selectedHostName)}
            </span>
            <span className="host-combobox-selected-name">
              {selectedHostName}
            </span>
          </span>
        ) : (
          <span className="host-combobox-placeholder">{S.SELECT_HOST.PLACEHOLDER}</span>
        )}
        {selectedHostId ? (
          <span
            role="button"
            className="host-combobox-clear"
            onMouseDown={handleClearClick}
            aria-label="Clear selection"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="host-combobox-chevron"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          className="host-combobox-panel"
          role="dialog"
          data-testid={`HC-panel-${dataTestId}`}
        >
          {/* Search bar */}
          <div className="host-combobox-search-bar">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="host-combobox-search-icon"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={S.SELECT_HOST.SEARCH_PLACEHOLDER_ALT}
              autoComplete="off"
              spellCheck={false}
              data-testid={`HC-search-${dataTestId}`}
            />
          </div>

          {/* Results list */}
          <div
            className="host-combobox-list"
            role="listbox"
            data-testid={`HC-list-${dataTestId}`}
          >
            {isLoading && (
              <div className="host-combobox-loading">{S.COMMON.SEARCHING}</div>
            )}

            {!isLoading &&
              options.map((opt) => {
                const isSelected = opt.value === selectedHostId;
                const subtitle = [opt.title, opt.department]
                  .filter(Boolean)
                  .join(" • ");
                return (
                  <div
                    key={opt.value}
                    className={`host-combobox-item${isSelected ? " selected" : ""}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={() => handleItemClick(opt)}
                    data-testid={`HC-item-${opt.value}`}
                  >
                    {/* Checkmark */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`host-combobox-check${isSelected ? " visible" : ""}`}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>

                    {/* Avatar */}
                    <div
                      className="host-combobox-avatar"
                      style={{ backgroundColor: btnColor }}
                    >
                      {getAvatarValue(opt.label)}
                    </div>

                    {/* Name + subtitle */}
                    <div className="host-combobox-info">
                      <div className="host-combobox-name">{opt.label}</div>
                      {subtitle && (
                        <div className="host-combobox-subtitle">{subtitle}</div>
                      )}
                    </div>
                  </div>
                );
              })}

            {!isLoading && options.length === 0 && searchTerm.length >= 3 && (
              <div className="host-combobox-empty">{S.SELECT_HOST.NO_HOSTS_FOUND}</div>
            )}

            {!isLoading &&
              options.length === 0 &&
              searchTerm.length < 3 &&
              searchTerm.length >= 0 && (
                <div className="host-combobox-empty">
                  {S.SELECT_HOST.MIN_CHARS_MESSAGE}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HostCombobox;
